import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Space, Tag, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { BannerActionDetailOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { CreativeDimensions } from '@/components/user_catalog/creative/CreativeDimensions';
import { formatDateTime } from '@/utils/format';
import {
  BannerActionKindTag,
  BannerSizeTag,
  TargetAvailabilityTag,
} from './BannerActionTags';
import type { CreativeModel } from './bannerActionPreview';

const { Text } = Typography;

interface BannerActionBasicInfoCardProps {
  record: BannerActionDetailOut;
  /** Crop and dimensions are read from the preview, never re-derived. */
  model: CreativeModel;
}

export const BannerActionBasicInfoCard = ({
  record,
  model,
}: BannerActionBasicInfoCardProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();

  const key = (suffix: string) => `user_catalog.banner_actions.${suffix}`;

  // Resolved by the backend from kind + target_ref; shaped per target type.
  // Discriminates structurally — the generated `type` tag is optional.
  const target = record.target;
  const targetLabel = !target
    ? null
    : 'identifier' in target
      ? target.identifier
      : 'collection_id' in target
        ? `${t(`user_catalog.collections.kind.${target.kind}`)} · ${target.slug}`
        : target.slug;

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: orDash(record.id != null && <Text code>{record.id}</Text>),
    },
    {
      key: 'name',
      label: t(key('fields.name')),
      children: orDash(record.name),
    },
    {
      key: 'kind',
      label: t(key('fields.kind')),
      children: <BannerActionKindTag value={record.kind} />,
    },
    {
      key: 'target',
      label: t(key('fields.target')),
      // A dead target may resolve to no label — its status must still show.
      children: orDash(
        (targetLabel || record.target_available != null) && (
          <Space size={4}>
            {targetLabel && <Text code>{targetLabel}</Text>}
            <TargetAvailabilityTag value={record.target_available} />
          </Space>
        )
      ),
    },
    {
      key: 'size',
      label: t(key('fields.size')),
      children: <BannerSizeTag value={record.size} showRatio />,
    },
    {
      key: 'dimensions',
      label: t(key('fields.dimensions')),
      children: orDash(
        model.dimensions && (
          <CreativeDimensions
            width={model.dimensions.width}
            height={model.dimensions.height}
            crop={model.crop}
          />
        )
      ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
    {
      key: 'deeplink',
      label: t(key('fields.deeplink')),
      span: 3,
      children: record.deeplink ? (
        <Text
          code
          copyable={{ text: record.deeplink }}
          ellipsis
          style={{ maxWidth: '100%' }}
        >
          {record.deeplink}
        </Text>
      ) : (
        // The image still renders in the app; the banner just does
        // nothing on tap — warn rather than hide.
        <Tag color="warning">{t(key('deeplink_none'))}</Tag>
      ),
    },
    {
      key: 'image_url',
      label: t(key('fields.image_url')),
      span: 3,
      children: orDash(
        model.imageUrl && (
          <Typography.Link
            href={model.imageUrl}
            target="_blank"
            rel="noreferrer"
          >
            <Text code ellipsis style={{ maxWidth: '100%' }}>
              {model.imageUrl}
            </Text>
          </Typography.Link>
        )
      ),
    },
  ];

  return (
    <Card title={t('base.sections.basic_info')}>
      <Descriptions
        {...verticalDescProps}
        column={{ xs: 1, sm: 2, md: 3 }}
        items={items}
      />
    </Card>
  );
};
